/* eslint-disable max-lines */
import { CalendarContainer, DatePicker, DatePickerProps } from 'react-datepicker';
import './styles/datepicker.scss';
import React, {
    ComponentProps, forwardRef, useMemo, useState,
} from 'react';
import {
    Box, Button, HStack, IconButton, Input, InputGroup, InputLeftElement,
    Text,
} from '@chakra-ui/react';
import { faAngleLeft, faCalendar } from '@fortawesome/pro-regular-svg-icons';
import {
    format, set, startOfMonth,
} from 'date-fns';
import { faAngleRight } from '@fortawesome/sharp-light-svg-icons';
import { FaIcon } from '../fa-icon';

type CustomInputProps = ComponentProps<typeof Input> & {
    overridePlaceholder?: string;
    showBlackPlaceholder?: boolean;
};

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    (props, ref) => (
        <InputGroup w="100%">
            <InputLeftElement color="gray.500" pointerEvents="none">
                <FaIcon icon={faCalendar} />
            </InputLeftElement>
            <Input
                ref={ref}
                {...props}
                placeholder={props.overridePlaceholder ?? props.placeholder ?? 'Select date'}
                _placeholder={{
                    color: props.showBlackPlaceholder === true ? 'black' : 'gray.500',
                }}
            />
        </InputGroup>
    )
);

type DatePickerV3Props = {
    size?: ComponentProps<typeof Input>['size'];
    boxProps?: ComponentProps<typeof Box>;
    inputProps?: ComponentProps<typeof Input>;
    overrideInputValue?: string;
} & DatePickerProps;

function CustomCalendarContainer({
    children,
    className,
    onButtonClick,
    buttonText,
}: {
    children: React.ReactNode;
    className?: string;
    onButtonClick?: () => void;
    buttonText?: string;
}) {
    return (
        <CalendarContainer className={className}>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {children}
                <Button mb={2} mx={2} size="sm" colorScheme="black" onClick={onButtonClick}>
                    {buttonText}
                </Button>
            </div>
        </CalendarContainer>
    );
}

export function DatePickerV3ExplicitNull({
    size = 'md',
    inputProps,
    boxProps,
    explicitNullText,
    selected,
    onChange,
    ...pickerProps
}: DatePickerV3Props & {
    selected: Date | 'PENDING_INPUT' | null;
    explicitNullText: string;
    onChange?: (date: Date | null) => void;
}) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<'day' | 'month'>('day');

    const actualMinDate: Date | undefined = useMemo(() => {
        if (pickerProps.minDate === undefined || mode === 'day' || !open) {
            return pickerProps.minDate;
        }

        return set(startOfMonth(pickerProps.minDate), {
            hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
        });
    }, [pickerProps.minDate, mode]);

    return (
        <Box {...boxProps}>
            <DatePicker
                open={open}
                onInputClick={() => setOpen(true)}
                onClickOutside={() => setOpen(false)}
                dateFormat="dd/MM/yyyy"
                renderCustomHeader={({
                    date,
                    decreaseMonth,
                    decreaseYear,
                    increaseMonth,
                    increaseYear,
                    prevMonthButtonDisabled,
                    prevYearButtonDisabled,
                    nextMonthButtonDisabled,
                    nextYearButtonDisabled,
                }) => (
                    <HStack
                        justifyContent="space-between"
                        px={2}
                    >
                        <IconButton
                            icon={<FaIcon icon={faAngleLeft} />}
                            aria-label="Previous"
                            isDisabled={mode === 'month' ? prevYearButtonDisabled : prevMonthButtonDisabled}
                            onClick={mode === 'month' ? decreaseYear : decreaseMonth}
                            rounded="full"
                            color="black"
                            variant="ghost"
                            size="sm"
                        />
                        <Text fontWeight="bold" fontSize="md" cursor="pointer" onClick={() => setMode('month')}>
                            {format(date, mode === 'month' ? 'yyyy' : 'MMM yyyy')}
                        </Text>
                        <IconButton
                            icon={<FaIcon icon={faAngleRight} />}
                            aria-label="Next"
                            isDisabled={mode === 'month' ? nextYearButtonDisabled : nextMonthButtonDisabled}
                            onClick={mode === 'month' ? increaseYear : increaseMonth}
                            rounded="full"
                            color="black"
                            variant="ghost"
                            size="sm"
                        />
                    </HStack>
                )}
                customInput={(
                    <CustomInput
                        size={size}
                        {...inputProps}
                        overridePlaceholder={
                            selected === 'PENDING_INPUT'
                                ? pickerProps.placeholderText
                                : explicitNullText
                        }
                        showBlackPlaceholder={selected === null}
                    />
                )}
                onChange={(date: (Date | null | [Date | null, Date | null] | Date[])) => {
                    if (
                        pickerProps.selectsMultiple
                        || pickerProps.selectsRange
                        || Array.isArray(date)
                    ) {
                        return;
                    }

                    if (mode === 'month') {
                        setMode('day');
                    } else {
                        onChange?.(date);
                        setOpen(false);
                    }
                }}
                selected={selected === 'PENDING_INPUT' ? null : selected}
                // eslint-disable-next-line react/no-unstable-nested-components
                calendarContainer={({ children, ...props }) => (
                    <CustomCalendarContainer
                        {...props}
                        onButtonClick={() => {
                            if (!pickerProps.selectsMultiple && !pickerProps.selectsRange) {
                                onChange?.(null);
                                setMode('day');
                            }

                            setOpen(false);
                        }}
                        buttonText={explicitNullText}
                    >
                        {children}
                    </CustomCalendarContainer>
                )}
                showMonthYearPicker={mode === 'month'}
                {...pickerProps}
                minDate={actualMinDate as Date}
            />
        </Box>
    );
}

export function DatePickerV3({
    size = 'md',
    inputProps,
    boxProps,
    selected,
    onChange,
    ...pickerProps
}: DatePickerV3Props & {
    selected: Date | null;
    onChange?: (date: Date | null) => void;
}) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<'day' | 'month'>('day');

    const actualMinDate: Date | undefined = useMemo(() => {
        if (pickerProps.minDate === undefined || mode === 'day' || !open) {
            return pickerProps.minDate;
        }

        return set(startOfMonth(pickerProps.minDate), {
            hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
        });
    }, [pickerProps.minDate, mode]);

    return (
        <Box {...boxProps}>
            <DatePicker
                open={open}
                onInputClick={() => setOpen(true)}
                onClickOutside={() => setOpen(false)}
                dateFormat="dd/MM/yyyy"
                renderCustomHeader={({
                    date,
                    decreaseMonth,
                    decreaseYear,
                    increaseMonth,
                    increaseYear,
                    prevMonthButtonDisabled,
                    prevYearButtonDisabled,
                    nextMonthButtonDisabled,
                    nextYearButtonDisabled,
                }) => (
                    <HStack
                        justifyContent="space-between"
                        px={2}
                    >
                        <IconButton
                            icon={<FaIcon icon={faAngleLeft} />}
                            aria-label="Previous"
                            isDisabled={mode === 'month' ? prevYearButtonDisabled : prevMonthButtonDisabled}
                            onClick={mode === 'month' ? decreaseYear : decreaseMonth}
                            rounded="full"
                            color="black"
                            variant="ghost"
                            size="sm"
                        />
                        <Text fontWeight="bold" fontSize="md" cursor="pointer" onClick={() => setMode('month')}>
                            {format(date, mode === 'month' ? 'yyyy' : 'MMM yyyy')}
                        </Text>
                        <IconButton
                            icon={<FaIcon icon={faAngleRight} />}
                            aria-label="Next"
                            isDisabled={mode === 'month' ? nextYearButtonDisabled : nextMonthButtonDisabled}
                            onClick={mode === 'month' ? increaseYear : increaseMonth}
                            rounded="full"
                            color="black"
                            variant="ghost"
                            size="sm"
                        />
                    </HStack>
                )}
                customInput={(
                    <CustomInput
                        size={size}
                        {...inputProps}
                    />
                )}
                onChange={(date: (Date | null | [Date | null, Date | null] | Date[])) => {
                    if (
                        pickerProps.selectsMultiple
                        || pickerProps.selectsRange
                        || Array.isArray(date)
                    ) {
                        return;
                    }

                    if (mode === 'month') {
                        setMode('day');
                    } else {
                        onChange?.(date);
                        setOpen(false);
                    }
                }}
                selected={selected}
                showMonthYearPicker={mode === 'month'}
                {...pickerProps}
                minDate={actualMinDate as Date}
            />
        </Box>
    );
}
